<template>
  <permission-gate
    moduleKey="cilindros"
    :showNoEditAlert="true"
  >
    <template #default="{ canEdit }">
      <main-breadcrumb
        :items="items"
        showBackButton
      ></main-breadcrumb>
      <main-buttons-bar :align="'space-between'">
        <template #left>
          <custom-title>Editar Engrane Troquel</custom-title>
        </template>
      </main-buttons-bar>

      <detail-container>
        <el-form
          ref="formRef"
          :model="cylinderAdapter"
          :rules="formRules"
          size="default"
        >
          <el-row :gutter="24">
            <custom-select
              v-model="selectedCilindroTipo"
              :label="'Tipo cilindro'"
              :spanColumn="12"
              :settings="{
                placeholder: 'Seleccionar cilindro',
                inputClass: { 'input-error': !validateCylinderAdapterFields.CodigoCilindroTipo },
                disabled: !canEdit,
                options: cilindroTipoList,
              }"
            ></custom-select>
          </el-row>

          <el-row :gutter="24">
            <custom-input-number
              v-model="cylinderAdapter.Dientes"
              :label="'Dientes'"
              :spanColumn="12"
              :settings="{
                placeholder: 'Cantidad de dientes',
                inputClass: [
                  'ancho_total',
                  { 'input-error': !validateCylinderAdapterFields.Dientes },
                ],
                min: 1,
                precision: 0,
                step: 1,
                controls: false,
                disabled: !canEdit,
              }"
            ></custom-input-number>

            <custom-fraction-mm-input
              v-model="cylinderAdapter.Paso"
              :label="'Paso'"
              :spanColumn="12"
              :settings="{
                placeholder: 'Valor en mm o fracción de pulgada (ej: 1/8)',
                inputClass: { 'input-error': !validateCylinderAdapterFields.Paso },
                disabled: !canEdit,
              }"
            ></custom-fraction-mm-input>
          </el-row>
        </el-form>

        <div
          v-if="canEdit"
          class="container"
        >
          <custom-confirm-buttons
            :disabled="false"
            :showLoading="false"
            :confirmText="'Guardar'"
            :cancelText="'Cancelar'"
            @on-cancel="onCancel"
            @on-confirm="onConfirm"
          ></custom-confirm-buttons>
        </div>
      </detail-container>
    </template>
  </permission-gate>
</template>

<script setup>
import {
  computed,
  onBeforeMount,
  reactive,
  ref,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElNotification } from 'element-plus';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import CustomConfirmButtons from '@/shared/components/custom_confirm_buttons.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomFractionMmInput from '@/shared/components/custom_fraction_mm_input.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import DetailContainer from '@/shared/layout/detail/detail_container.vue';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import { useCilindroTipoStore } from '@/stores/cilindro-tipo/cilindro-tipo';
import { useCylinderAdapterStore } from '../stores/cylinder_adapters_store.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import PermissionGate from '@/shared/components/permission_gate.vue';

const cilindroTipoStore = useCilindroTipoStore();
const cylinderAdapterStore = useCylinderAdapterStore();
const route = useRoute();
const router = useRouter();

const { cilindroTipoList } = storeToRefs(cilindroTipoStore);
const { cylinderAdapter, validateCylinderAdapterFields } = storeToRefs(cylinderAdapterStore);
const {
  findOneCilindroAdaptadorHTTP,
  resetCilindroAdaptador,
  updateCilindroAdaptadorHTTP,
  validarCampos,
  isDuplicateError,
} = cylinderAdapterStore;

const items = [
  { label: 'Inicio', to: '/page/cylinder-adapters/list', replace: false },
  { label: 'Editar', to: null, replace: false },
];

const formRef = ref(null);

const formRules = reactive({
  CodigoCilindroTipo: [
    {
      validator: (rule, value, callback) => {
        if (!value) {
          callback(new Error('Seleccionar tipo de cilindro'));
        } else {
          callback();
        }
      },
      trigger: 'change',
    },
  ],
  Dientes: [
    { required: true, message: 'Ingresar cantidad de dientes', trigger: 'change' },
    {
      validator: (rule, value, callback) => {
        if (value && value > 0) {
          callback();
        } else {
          callback(new Error('La cantidad de dientes debe ser mayor a 0'));
        }
      },
      trigger: 'change',
    },
  ],
  Paso: [
    { required: true, message: 'Ingresar paso', trigger: 'change' },
    {
      validator: (rule, value, callback) => {
        if (value && value > 0) {
          callback();
        } else {
          callback(new Error('El paso debe ser mayor a 0'));
        }
      },
      trigger: 'change',
    },
  ],
});

const selectedCilindroTipo = computed({
  get: () =>
    cilindroTipoList.value.find((it) => it.Codigo === cylinderAdapter.value.CodigoCilindroTipo) ||
    null,
  set: (value) => {
    cylinderAdapter.value.CodigoCilindroTipo = value?.Codigo || null;
    cylinderAdapter.value.CilindroTipo = value || null;
    validateCylinderAdapterFields.value.CodigoCilindroTipo = true;
  },
});

const onCancel = () => {
  resetCilindroAdaptador();
  if (formRef.value) {
    formRef.value.resetFields();
  }
  router.push('/page/cylinder-adapters/list');
};

onBeforeMount(async () => {
  await cilindroTipoStore.firstCilindroTipo();

  const codigo = route.params.id;
  if (codigo && +codigo > 0) {
    await findOneCilindroAdaptadorHTTP(codigo);
  }
});

const onConfirm = async () => {
  if (!formRef.value) return;

  formRef.value.validate(async (valid) => {
    if (!valid) {
      ElNotification({
        title: 'Engrane Troquel',
        message: 'Por favor, revise los campos ingresados.',
        type: 'warning',
      });
      return;
    }

    if (!validarCampos()) {
      ElNotification({
        title: 'Engrane Troquel',
        message: 'Por favor, completar todos los campos correctamente.',
        type: 'warning',
      });
      return;
    }

    try {
      useLoading().openLoading();
      await updateCilindroAdaptadorHTTP();
      useMessageSuccess().open('Los datos se han actualizado exitosamente!');
      router.push('/page/cylinder-adapters/list');
    } catch (err) {
      if (isDuplicateError(err)) {
        return;
      }
      useMessageError().open(
        err.response?.data?.message || 'Ha ocurrido un error al intentar actualizar los datos.',
      );
    } finally {
      useLoading().closeLoading();
    }
  });
};
</script>

<style scoped>
.custom-label {
  font-weight: 700;
  color: #111;
}

.container {
  display: flex;
  justify-content: flex-end;
}
</style>
